import { useId, useState, type ReactNode } from 'react';
import type { FacetEntry } from '../types';
import { COLOR_KO, FIELD_KO, koOrOther } from '../vocab';
import { Icon } from './Icon';

/** 패싯 색 견본 — 측정 색 계열의 대표색(원단 색이라 테마와 무관). 화면에서 채도 있는 색은 원단 · 팔레트뿐(컨셉 §2). */
const COLOR_HEX: Record<string, string> = {
  white: '#F5F5F5', black: '#1C1C1C', gray: '#878787', beige: '#DCC9A8',
  brown: '#6B4A2F', red: '#C1272D', orange: '#E87722', yellow: '#E8D24A',
  green: '#2E7D32', blue: '#1F5FBF', purple: '#7B3FA0', pink: '#E8A0B4',
};

export interface FacetSelection {
  color?: string;
  pattern?: string;
  manufacturer?: string;
  in_stock?: boolean;
}

const SELECTABLE = ['color', 'pattern', 'manufacturer'] as const;
type SelKey = typeof SELECTABLE[number];
/** 처음에 보이는 개수 — 넘치면 「모두 보기」 */
const FIRST = 6;

interface Props {
  facets: Record<string, FacetEntry[] | undefined>;
  selected: FacetSelection;
  onChange: (next: FacetSelection) => void;
  /** 축 순서 — 검색 결과는 패턴 크기(보기 전용)까지, 전체 원단은 색 · 패턴 · 제조사 */
  keys?: string[];
  /** 레일 맨 위에 더 넣을 묶음(전체 원단의 등록 기간) */
  leading?: ReactNode;
  /** 머리 칩에 함께 보일 추가 조건(등록 기간) — 누르면 해제 */
  extraChips?: Array<{ key: string; label: string; clear: () => void }>;
  /** 「필터 초기화」 — 주소 한 번에 지우도록 화면이 직접 맡을 때 */
  onReset?: () => void;
  /** 축 묶음 뒤 · 재고 앞에 더 넣을 묶음(소재 · 단가) */
  trailing?: ReactNode;
  /** 휴대폰 아래 시트 안 — 접기 버튼 · 적용된 칩 · 초기화 버튼은 시트가 맡는다 (R3-9) */
  inSheet?: boolean;
  /** 색 근거가 없어 어느 색에도 없는 원단 수 — 0 보다 크면 색 견본 아래에 알린다(전체 원단, 2026-10-18) */
  noColor?: number;
  /**
   * 비로그인 — 제조사 · 재고 조건은 로그인한 뒤 쓸 수 있다(사양 §26.3 기본안 · 2026-10-18 서버: 비로그인이 쓰면 401).
   * 주면 제조사 묶음과 「재고 보유」 대신 이 안내를 그린다(Codex 8차 B7)
   */
  supplyLocked?: ReactNode;
}

/** 값 → 화면 이름. 제조사는 고유 이름이라 그대로, 나머지는 사전(없으면 「기타」 — 영문 원문을 내보내지 않는다) */
export function facetLabel(key: string, value: string): string {
  return key === 'manufacturer' ? value : koOrOther(value, key);
}

/**
 * 색 견본 격자 (계획서 U3 · 루브릭 R3-1) — 동그란 견본 + 한국어 색 이름 + 건수, 누르면 켜짐/꺼짐(aria-pressed).
 * 0건 색은 서버가 빼서 오지 않는다(고른 색만 0건이어도 남겨 끌 수 있게). 견본 테두리는 --swatch-ring(바탕 대비 3:1 이상)이라
 * 흰색 견본은 밝은 테마에서, 검정 견본은 어두운 테마에서도 동그라미가 보인다. 켠 견본은 체크 아이콘 + 굵은 글자 — 색만으로 알리지 않는다.
 */
function ColorGrid({ entries, picked, onToggle }: { entries: FacetEntry[]; picked?: string; onToggle: (v: string) => void }) {
  return (
    <div className="sw-grid">
      {entries.map((e) => {
        const on = picked === e.value;
        const name = facetLabel('color', e.value);
        return (
          <button type="button" key={e.value} className={on ? 'sw-tile on' : 'sw-tile'} aria-pressed={on}
                  title={on ? `${name} — 누르면 해제` : `${name}만 보기`} onClick={() => onToggle(e.value)}>
            <span className={`sw-dot sw-${e.value}`} style={{ background: COLOR_HEX[e.value] ?? 'var(--line-strong)' }} aria-hidden="true">
              {on && <Icon name="check" size={14} />}
            </span>
            <span className="sw-name">{COLOR_KO[e.value] ? name : '기타'}</span>
            <span className="sw-n">{e.count.toLocaleString()}<span className="sr-only">건</span></span>
          </button>
        );
      })}
    </div>
  );
}

export default function Facets({ facets, selected, onChange, keys: keysIn = ['color', 'pattern', 'pattern_scale', 'manufacturer'], leading, extraChips = [], onReset, trailing, inSheet, noColor = 0, supplyLocked }: Props) {
  const keys = supplyLocked ? keysIn.filter((k) => k !== 'manufacturer') : keysIn;
  // 디자인 리뷰 2회차 M2: 1080px 이하에서는 패싯이 결과 위에 733px 를 차지해 첫 카드가
  // 화면 밖으로 밀렸다. 좁은 폭에서는 접어 두고 「필터」 버튼으로 연다 (CSS 가 폭을 판단).
  const [open, setOpen] = useState(false);
  const [more, setMore] = useState<Record<string, boolean>>({});
  const uid = useId();

  const toggle = (key: SelKey, value: string) => {
    onChange({ ...selected, [key]: selected[key] === value ? undefined : value });
  };

  const chips: { key: string; label: string; clear: () => void }[] = [...extraChips];
  for (const k of SELECTABLE) {
    const v = selected[k];
    if (v) chips.push({ key: k, label: `${FIELD_KO[k]} · ${facetLabel(k, v)}`, clear: () => onChange({ ...selected, [k]: undefined }) });
  }
  if (selected.in_stock) chips.push({ key: 'in_stock', label: '재고 보유', clear: () => onChange({ ...selected, in_stock: undefined }) });
  const hasFilter = chips.length > 0;

  return (
    <aside className={inSheet ? 'facets open in-sheet' : open ? 'facets open' : 'facets'} aria-label="필터">
      {!inSheet && (
        <button type="button" className="facets-toggle btn sm"
                aria-expanded={open} aria-controls={`${uid}-body`} onClick={() => setOpen((o) => !o)}>
          <Icon name="filter" size={16} /> 필터{hasFilter ? ` (${chips.length})` : ''} {open ? '접기' : '열기'}
        </button>
      )}

      {hasFilter && !inSheet && (
        <div className="facet-chips" aria-label="적용된 필터">
          {chips.map((c) => (
            <button type="button" className="chip-x" key={c.key} onClick={c.clear}
                    aria-label={`${c.label} 필터 해제`}>
              {c.label} <Icon name="close" size={12} />
            </button>
          ))}
        </div>
      )}

      <div className="facets-body" id={`${uid}-body`}>
        {leading}

        {keys.map((key) => {
          const canSelect = (SELECTABLE as readonly string[]).includes(key);
          // 리뷰 7회차 관찰 ③: 다른 조건 때문에 고른 값의 건수가 0 이 되면 서버 목록에서 빠져 칩으로만 풀 수 있었다 —
          // 고른 값은 0건이어도 목록에 남긴다(체크를 풀 수 있게)
          const picked = canSelect ? selected[key as SelKey] : undefined;
          const base = facets[key] ?? [];
          const entries = picked && !base.some((e) => e.value === picked) ? [{ value: picked, count: 0 }, ...base] : base;
          // 색 근거 없는 원단만 있으면(색 보정 없는 촬영의 측정 기반 색) 견본 없이 안내만 남긴다 — 색 칸이 말없이 사라지지 않게
          if (!entries.length && !(key === 'color' && noColor > 0)) return null;
          if (key === 'color' && canSelect) {
            return (
              <fieldset className="facet facet-colors" key={key}>
                <legend>{FIELD_KO[key]}</legend>
                {entries.length > 0 && <ColorGrid entries={entries} picked={selected.color} onToggle={(v) => toggle('color', v)} />}
                {/* 한 원단이 여러 색에 잡힐 수 있어 색 건수 합으로는 알 수 없다 — 서버가 따로 센다(facets.color_unknown) */}
                {noColor > 0 && <p className="facet-note">색 미확인 원단 {noColor.toLocaleString()}건은 색으로 거를 수 없습니다.</p>}
              </fieldset>
            );
          }
          const shown = more[key] ? entries : entries.slice(0, FIRST);
          return (
            <fieldset className="facet" key={key}>
              <legend>{FIELD_KO[key] ?? '기타'}</legend>
              {shown.map((e) => (
                <label className={canSelect ? 'facet-row' : 'facet-row static'} key={e.value}>
                  {canSelect && (
                    // readOnly + onClick 은 키보드로 조작할 수 없었다 (리뷰 E2). 실제 체크박스로.
                    <input type="checkbox"
                           checked={selected[key as SelKey] === e.value}
                           onChange={() => toggle(key as SelKey, e.value)} />
                  )}
                  {(key === 'color') && (
                    <span className="facet-sw" style={{ background: COLOR_HEX[e.value] ?? 'var(--line-strong)' }} />
                  )}
                  <span className="facet-v">{facetLabel(key, e.value)}</span>
                  <span className="facet-n" aria-label={`${e.count}건`}>{e.count.toLocaleString()}</span>
                </label>
              ))}
              {entries.length > FIRST && (
                <button type="button" className="facet-more" aria-expanded={!!more[key]}
                        onClick={() => setMore((m) => ({ ...m, [key]: !m[key] }))}>
                  <Icon name={more[key] ? 'minus' : 'plus'} size={14} />
                  {more[key] ? '접기' : `모두 보기 (${entries.length})`}
                </button>
              )}
            </fieldset>
          );
        })}

        {trailing}

        {supplyLocked ?? (
          <fieldset className="facet">
            <legend>재고</legend>
            <label className="facet-row">
              <input type="checkbox" checked={!!selected.in_stock}
                     onChange={() => onChange({ ...selected, in_stock: selected.in_stock ? undefined : true })} />
              <span className="facet-v">재고 보유</span>
            </label>
          </fieldset>
        )}

        {hasFilter && !inSheet && (
          <button type="button" className="btn ghost sm facets-reset"
                  onClick={() => (onReset ? onReset() : onChange({}))}>
            <Icon name="refresh" size={16} /> 필터 초기화
          </button>
        )}
      </div>
    </aside>
  );
}
